import { Stack, Typography } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Label, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useCurrentPng } from 'recharts-to-png';
import { convertDecimals, roundTwoDecimal } from '../../calculation/util';
import { EUnitOfMeasureImperialLabel, EUnitOfMeasureMetricLabel } from '../../conversion/enum';
import Table from '../../shared/component/Table';
import { EFSType, EStableSlopeValidationType, EUnitOfMeasure } from '../../shared/enum';
import { IPhaseOneOutput } from '../../shared/iProject';
import variable from '../../shared/style/variable';
import { useAppDispatch, useAppSelector } from '../../store/hook';
import { setPreliminaryCalculationImage } from '../../store/Image/imageSlice';
import { selectProject } from '../../store/project/projectSlice';
import { getInterventionSlopeValidationTable } from '../result/util';
import fsDefaultLegend from './image/fsDefaultLegend.png';
import fsSeismicLegend from './image/fsSeismicLegend.png';
import fsSeismicMinLegend from './image/fsSeismicMinLegend.png';
import fsStaticLegend from './image/fsStaticLegend.png';
import fsStaticMinLegend from './image/fsStaticMinLegend.png';
import initialWaterLegend from './image/initialWaterLegend.png';
import { Legend } from './Utils';

const color = {
    text: 'black',
    fsStatic: '#068837',
    fsSeismic: '#FBBC33',
    fsDefault: '#314A9A',
    fsStaticMin: '#068837',
    fsSeismicMin: '#FBBC33',
    initialWaterTableDepth: '#309CAE',
    campaignPlan: 'black',
};

const fsDefault = 1;
const campaignPlan = 0;

interface IPreliminaryCalculationProps {
    withExport?: boolean;
}

const CustomizedDot = (props: any) => {
    let fill, condition;
    switch (props.type) {
        case EFSType.Seismic:
            condition = roundTwoDecimal(props.payload.fsSeismic) == props.min;
            fill = color.fsSeismic;
            break;
        case EFSType.Static:
            condition = roundTwoDecimal(props.payload.fsStatic) == props.min;
            fill = color.fsStatic;
            break;
    }
    return (
        condition && (
            <svg x={props.cx - 5} y={props.cy - 5} width={10} height={10} fill={fill} viewBox='0 0 1024 1024'>
                <circle cx='512' cy='512' r='512' />
            </svg>
        )
    );
};

const CustomTooltip = (props: any) => {
    const { t } = useTranslation();
    const project = useAppSelector(selectProject);

    return (
        props.active &&
        props.payload &&
        props.payload.length && (
            <Stack direction={'column'} style={{ backgroundColor: variable.white, borderRadius: 10, padding: 15, boxShadow: '5px 10px 18px #888888' }}>
                <Stack style={{ color: color.initialWaterTableDepth }}>{`${t('depth', {
                    unitOfMeasure: project.data?.projectInfo.unitOfMeasure == EUnitOfMeasure.Metric ? EUnitOfMeasureMetricLabel.meter : EUnitOfMeasureImperialLabel.foot,
                })}: ${roundTwoDecimal(props.payload[0].payload.dw)}`}</Stack>
                <Stack style={{ color: color.fsStatic }}>{`${t('fsStatic')}: ${roundTwoDecimal(props.payload[0].payload.fsStatic)}`}</Stack>
                <Stack style={{ color: color.fsSeismic }}> {`${t('fsSeismic')}: ${roundTwoDecimal(props.payload[0].payload.fsSeismic)}`}</Stack>
            </Stack>
        )
    );
};

export default (props: IPreliminaryCalculationProps) => {
    const { t } = useTranslation();

    const project = useAppSelector(selectProject);
    const dispatch = useAppDispatch();

    const [getPng, { ref }] = useCurrentPng();

    const phaseOneOutputs: IPhaseOneOutput = project.data!.phaseOneOutputs!;

    let preliminaryInterventionSlopeValidationTable = getInterventionSlopeValidationTable(convertDecimals(phaseOneOutputs), EStableSlopeValidationType.Pre);

    const legendData = [
        { name: 'fsStatic', logo: fsStaticLegend },
        project.data?.load?.hasSeismicLoads ? { name: 'fsSeismic', logo: fsSeismicLegend } : undefined,
        { name: 'fsDefault', logo: fsDefaultLegend, value: 1 },
        { name: 'minFsStaticConditionsWithValue', logo: fsStaticMinLegend, value: roundTwoDecimal(project.data?.load?.minFsStaticConditions!) },
        project.data?.load?.hasSeismicLoads ? { name: 'minFsSeismicConditionsWithValue', logo: fsSeismicMinLegend, value: roundTwoDecimal(project.data?.load?.minFsSeismicConditions!) } : undefined,
        {
            name: 'initialGroudwaterWithValue',
            logo: initialWaterLegend,
            value: `${project.data?.slope?.initialWaterTableDepth} ${
                project.data?.projectInfo.unitOfMeasure == EUnitOfMeasure.Metric ? EUnitOfMeasureMetricLabel.meter : EUnitOfMeasureImperialLabel.foot
            }`,
        },
    ];

    React.useEffect(() => {
        if (props.withExport) {
            setTimeout(async () => {
                const imgSource = await getPng();
                dispatch(setPreliminaryCalculationImage(imgSource!));
            }, 2000);
        }
    }, []);

    return (
        <Stack direction={'row'} height={'100%'} spacing={10} style={{ backgroundColor: variable.white }}>
            <Stack direction={'column'} gap={1} flex={1} alignItems={'baseline'}>
                <Typography padding={'1rem 1rem'} style={{ textAlignLast: 'center' }} fontSize={'0.9rem'} color={variable.black}>
                    {t('preliminaryCalculationInfo')}
                </Typography>
                <Legend data={legendData} />
                <Table data={preliminaryInterventionSlopeValidationTable}></Table>
            </Stack>
            <Stack flex={2}>
                <Typography style={{ width: 180, textAlign: 'end', fontSize: '0.8rem' }}>{t('campaignPlan')}</Typography>
                <ResponsiveContainer width={500} height={400}>
                    <LineChart layout='vertical' data={phaseOneOutputs?.phaseOneOutputFSs} ref={ref}>
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis
                            type='number'
                            allowDecimals
                            tickFormatter={value => roundTwoDecimal(value).toString()}
                            orientation='bottom'
                            style={{
                                fontSize: '0.6rem',
                                color: color.text,
                                fontFamily: 'Roboto, sans-serif',
                            }}>
                            <Label value={t('loadInputSafetySection')} fontFamily='Roboto, sans-serif' fontSize={'0.6rem'} offset={-5} position='insideBottom' />
                        </XAxis>
                        <YAxis
                            dataKey='dw'
                            interval={0}
                            ticks={Array.from({ length: project.data?.slope?.impermeableLayerDepth! + 1 }, (_, i) => i)}
                            domain={[0, project.data?.slope?.impermeableLayerDepth!]}
                            tickFormatter={value => roundTwoDecimal(value).toString()}
                            type='number'
                            style={{
                                fontSize: '0.6rem',
                                color: color.text,
                                fontFamily: 'Roboto, sans-serif',
                            }}>
                            <Label
                                value={t('waterTableDepth', {
                                    unitOfMeasure: project.data?.projectInfo.unitOfMeasure == EUnitOfMeasure.Metric ? EUnitOfMeasureMetricLabel.meter : EUnitOfMeasureImperialLabel.foot,
                                })}
                                angle={-90}
                                fontSize={'0.6rem'}
                                fontFamily='Roboto, sans-serif'
                                offset={15}
                                position='insideLeft'
                            />
                        </YAxis>
                        <Tooltip content={<CustomTooltip />} />
                        <ReferenceLine y={project.data?.slope?.initialWaterTableDepth!} stroke={color.initialWaterTableDepth} strokeDasharray='6 6' strokeWidth={3} />
                        <ReferenceLine x={fsDefault} stroke={color.fsDefault} strokeDasharray='6 6' strokeWidth={3} />
                        <ReferenceLine x={project.data?.load?.minFsStaticConditions} stroke={color.fsStaticMin} strokeDasharray='2 4 10 2' strokeWidth={1.5} />
                        <ReferenceLine y={campaignPlan} stroke={color.campaignPlan} opacity={0.3} strokeWidth={1} />
                        <Line
                            dataKey='fsStatic'
                            isAnimationActive={!props.withExport}
                            stroke={color.fsStatic}
                            dot={<CustomizedDot type={EFSType.Static} min={project.data?.load?.minFsStaticConditions} />}
                            strokeWidth={3}
                        />
                        {project.data?.load?.hasSeismicLoads && (
                            <>
                                <ReferenceLine x={project.data?.load?.minFsSeismicConditions} stroke={color.fsSeismicMin} strokeDasharray='2 4 10 2' strokeWidth={1.5} />
                                <Line
                                    dataKey='fsSeismic'
                                    isAnimationActive={!props.withExport}
                                    stroke={color.fsSeismic}
                                    dot={<CustomizedDot type={EFSType.Seismic} min={project.data?.load?.minFsSeismicConditions} />}
                                    strokeWidth={3}
                                />
                            </>
                        )}
                    </LineChart>
                </ResponsiveContainer>
            </Stack>
        </Stack>
    );
};
